import { Form, Formik, FormikValues } from "formik";
import {
  setApplicationStep,
  setReduxPhone,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import CryptoJourney from "./CryptoJourney";
import ApplicationNavigation from "./ApplicationNavigation";
import { useAppDispatch } from "src/store/store";
import AboutYou from "./AboutYou";
import { trackEvent } from "src/Amplitude";
import { CreateSfAccountPayloadDto } from "src/store/features/api/generated";
import AboutYourEntity from "./AboutYourEntity";
import useApplicationFormConfig from "./utils/useApplicationFormConfig";
import React from "react";
import { Grid } from "@mui/material";
import LoadingComponent from "./LoadingComponent";
import { cloneDeep } from "lodash";

const ApplicationForm = () => {
  const {
    details: { phone: signupPhoneNumber },
    referralSignUpCode,
  } = useUserSlice();

  const [validationSchema] = useApplicationFormConfig();

  const {
    applicationStep,
    address: { country, state, zipCode, fullAddress, street, unitNumber, city },
    phone,
    nationality,
    ssn,
    preferredName,
    birthdate: { day, month, year },
    cryptoJourney: {
      defaultCurrency,
      investmentExp,
      wealthSource,
      investmentPlan,
      portfolioValue,
      occupation,
      industry,
      frequency,
      hearAboutUs,
      otherWealthSource,
      accountPurpose,
    },
    entityDetails: {
      name: entityName,
      id: entityId,
      recordType: entityType,
      natureOfBusiness,
      companyType,
    },
    uploadedFiles,
    entityDetails,
    ignoreBiometrics,
  } = useOnboarding();

  const initialValues = {
    birthDay: day,
    birthMonth: month,
    birthYear: year,
    ssn,
    preferredName,
    country: country || "",
    city: city || "",
    address: fullAddress || "",
    state: state || "",
    postCode: zipCode || "",
    streetAddress: street || "",
    unitApartment: unitNumber || "",
    phone: phone,
    occupation: occupation,
    industry: industry,
    frequency: frequency,
    hearAboutUs: hearAboutUs,
    otherWealthSource: otherWealthSource,
    referralCode: referralSignUpCode,
    defaultCurrency: defaultCurrency,
    wealthSource: wealthSource,
    investmentExp: investmentExp,
    portfolioValue: portfolioValue,
    accountPurpose: accountPurpose,
    nationality,
    entityName,
    entityId,
    entityType,
    natureOfBusiness,
    companyType,
  };
  const [submitForm, { isLoading }] = api.useCreateSalesForceAccountMutation();
  const dispatch = useAppDispatch();

  // Existing signup flow already hydrates this; only push into Redux when present so we do not clear a draft phone for legacy accounts with no stored number.
  React.useEffect(() => {
    if (signupPhoneNumber?.trim()) {
      dispatch(setReduxPhone(signupPhoneNumber));
    }
  }, [signupPhoneNumber, dispatch]);

  const formatUnitNumber = (unitNumber: string | null | undefined, street: string) => {
    if (unitNumber) return `${unitNumber}/${street}`;
    return street;
  };

  const handleSubmit = async (
    values: FormikValues,
    setFieldError: (field: string, message: string | undefined) => void,
  ) => {
    const {
      hearAboutUs,
      occupation,
      otherWealthSource,
      referralCode,
      frequency: investmentFrequency,
      streetAddress,
      country,
      state,
      postCode: zipCode,
    } = values;

    // if country is United States, replace with United States of America (USA)
    const countryToSubmit =
      country === "United States" ? "United States of America (USA)" : country;

    if (defaultCurrency && investmentExp && wealthSource.length !== 0) {
      const phoneToSubmit = phone.replace(/[\s()-]/g, "");

      // Clone imutable object
      const editableEntityDetails = cloneDeep(entityDetails);

      if (entityDetails && entityDetails.address.country === "United States") {
        // Map entityDetails.address.country to be United States of America (USA)
        editableEntityDetails.address.country = "United States of America (USA)";
      }

      const createSfAccountPayloadDto: CreateSfAccountPayloadDto = {
        phone: phoneToSubmit,
        nationality,
        ssn,
        preferredName,
        dob: `${year}-${month}-${day}`,
        defaultCurrency,
        hearAboutUs,
        initialInvestment: investmentPlan ?? "$500-$10K",
        existingCryptoHoldings: portfolioValue ?? "$0-$10K",
        accountPurpose: accountPurpose ?? [],
        investmentExp,
        occupation,
        otherWealthSource,
        referralCode,
        investmentFrequency,
        industry,
        tandc: true,
        type: "Individual",
        wealthSource,
        address: {
          city: city || "",
          street: formatUnitNumber(unitNumber, streetAddress),
          country: countryToSubmit,
          state,
          zipCode,
        },
        entityDetails: editableEntityDetails,
        ignoreBiometrics,
        documents: uploadedFiles.map((file) => file.contentVersionId),
      };

      await submitForm({
        createSfAccountPayloadDto,
      })
        .unwrap() // Get the promise that reflects the actual API outcome
        .then((successPayload) => {
          // Move the user to the next step
          dispatch(setApplicationStep("applicationSuccess"));

          // Amplitude Tracking
          const { phone, address, ssn, ...payload } = createSfAccountPayloadDto;
          trackEvent(
            "Application Form Submission",
            { ...payload, ...successPayload },
            "Onboarding",
          );

          // Invalidate the user causing a refetch of user data as soon this step finishes
          dispatch(api.util.invalidateTags(["getUserDataTag"]));
        })
        .catch((error) => {
          window.alert(
            "Something went wrong while submitting the application form. Please try again later.",
          );
          console.error(error);
        });
    } else {
      // Some fields are missing check what they are and throw an error
      if (!defaultCurrency) setFieldError("defaultCurrency", "Please pick a currency");
      if (!investmentExp)
        setFieldError("investmentExp", "Please select your investment experience");
      if (wealthSource.length === 0)
        setFieldError("wealthSource", "Please select one or more source of income");
      throw new Error("Please fill all the required fields");
    }
  };

  return (
    <div>
      {isLoading ? (
        <Grid container display="flex" alignItems="center" height="70vh">
          <LoadingComponent message={"Submitting your application..."} />
        </Grid>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}>
          <Form>
            {applicationStep === "aboutYourEntity" && <AboutYourEntity />}
            {applicationStep === "aboutYou" && <AboutYou />}
            {applicationStep === "cryptoJourney" && <CryptoJourney />}
            <ApplicationNavigation />
          </Form>
        </Formik>
      )}
    </div>
  );
};

export default ApplicationForm;
